/**
 * Task 6 - "Find your size" size guide.
 *
 * Previously the button ran, inline:
 *     document.querySelector('.ks-calculator-modal-button').click()
 * which throws a TypeError and silently does nothing on every product where the
 * Kiwi Sizing app has not rendered its trigger - i.e. products whose template
 * carries no Kiwi app block, and products Kiwi has no chart assigned for.
 *
 * This replaces that with an ordered fallback chain:
 *   1. Kiwi Sizing app trigger, if the app rendered one
 *   2. MP Size Chart app trigger, if that app rendered one
 *   3. The theme's own size popup (snippets/find-size-popup.liquid), whose
 *      open/close handling had been commented out in global.js
 *
 * If none of the three is available the button is hidden rather than left on the
 * page as a dead link.
 */
(function () {
  'use strict';

  var APP_TRIGGERS = [
    // Kiwi Sizing
    '.ks-calculator-modal-button',
    '.ks-chart-modal-button',
    '.kiwi-sizing-button',
    '[class*="kiwi-size"][class*="button"]',
    // MP Size Chart / Size Guide
    '#mp-size-chart-button',
    '.mp-size-chart-trigger',
    '[class*="mpsizechart"]',
    '[class*="mp-size-chart"] button',
  ];

  var HIDDEN_TRIGGER_CLASS = 'js-size-trigger-hidden';

  function findAppTrigger() {
    for (var i = 0; i < APP_TRIGGERS.length; i++) {
      var el;
      try {
        el = document.querySelector(APP_TRIGGERS[i]);
      } catch (e) {
        continue;
      }
      if (!el) continue;
      // A trigger we hid on purpose is still a perfectly good click target.
      if (el.classList.contains(HIDDEN_TRIGGER_CLASS)) return el;
      // One that is merely absent from layout has not really rendered yet.
      if (el.offsetParent !== null || el.getClientRects().length > 0) return el;
    }
    return null;
  }

  /**
   * Three size-guide systems are live on this store at once: the theme's own
   * "Find your size" button, the Kiwi Sizing app, and the MP Size Chart app
   * (whose configured button label is "Size guide"). Each renders its own
   * trigger, which is why the label differed from product to product.
   *
   * Only the theme's button is left visible. The app triggers stay in the DOM,
   * hidden, so our button can still click them to open their modal - hiding
   * them is a presentation change, not a functional one.
   */
  var TRIGGER_TEXT = /^(size\s*guides?|size\s*charts?|find\s+your\s+size)$/i;

  function hideDuplicateTriggers() {
    var scope = document.querySelector('.product__info-wrapper, .product, main');
    if (!scope) return;

    scope.querySelectorAll('button, a, div[role="button"], span[role="button"]').forEach(function (el) {
      if (el.closest('.find_size_popup')) return;          // our own button
      if (el.closest('.size_button_wrapper')) return;      // inside our popup
      if (el.closest('.header, .footer, nav, .list-menu')) return; // menu links

      var text = (el.textContent || '').trim();
      if (!TRIGGER_TEXT.test(text)) return;

      el.classList.add(HIDDEN_TRIGGER_CLASS);
      el.style.setProperty('display', 'none', 'important');
    });
  }

  function themePopup() {
    var wrapper = document.querySelector('.size_button_wrapper');
    if (!wrapper) return null;
    var card = wrapper.querySelector('.size_button_wrapper_card');
    if (!card) return null;

    // The card always holds a close button and a "Size" heading. Anything else
    // is content contributed by the page assigned to the block.
    //
    // Do NOT measure text here: on this store that page embeds a Kiwi chart
    // (<div id="KiwiSizingChart" data-sizing-id="...">), which is empty markup
    // until Kiwi's script fills it in. A text-length check would reject a popup
    // that is about to become perfectly usable.
    var contentNodes = Array.prototype.filter.call(card.children, function (el) {
      return !el.classList.contains('size_button_close') && !el.classList.contains('size_heading');
    });
    if (!contentNodes.length) return null;

    // A lone <script> tag is not content.
    var meaningful = contentNodes.some(function (el) {
      return el.tagName !== 'SCRIPT';
    });
    return meaningful ? wrapper : null;
  }

  function openThemePopup(wrapper) {
    wrapper.style.display = 'flex';
    document.body.classList.add('size_pop_open');
  }

  function closeThemePopup(wrapper) {
    wrapper.style.display = 'none';
    document.body.classList.remove('size_pop_open');
  }

  function hasAnySizeGuide() {
    return Boolean(findAppTrigger() || themePopup());
  }

  function bindCloseHandlers() {
    var wrapper = document.querySelector('.size_button_wrapper');
    if (!wrapper || wrapper.dataset.sizeGuideBound === 'true') return;
    wrapper.dataset.sizeGuideBound = 'true';

    var closeButton = wrapper.querySelector('.size_button_close');
    if (closeButton) {
      closeButton.addEventListener('click', function (event) {
        event.preventDefault();
        closeThemePopup(wrapper);
      });
    }

    // Click on the dimmed overlay, outside the card, closes it too.
    wrapper.addEventListener('click', function (event) {
      if (event.target === wrapper) closeThemePopup(wrapper);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && document.body.classList.contains('size_pop_open')) {
        closeThemePopup(wrapper);
      }
    });
  }

  /**
   * The size chart is cut off on a phone.
   *
   * Both size-chart apps drop a merchant-authored <table> straight into their
   * modal with nothing to scroll it, and the theme's own popup does the same
   * with the page content assigned to it. On an iPhone the chart is wider than
   * the modal, so the right-hand columns ("Head Scarf / Shela") are simply
   * unreachable - the report that started this.
   *
   * Making the table its own horizontal scroll container fixes it without
   * touching the chart itself: no columns dropped, no font shrunk to
   * illegibility, no numbers reflowed. display:block is what does the work - it
   * turns the table into a block box whose inner table is free to be wider than
   * it, which is what there is to scroll. Applied inline because the apps ship
   * their own stylesheets with !important.
   */
  var SCROLL_FLAG = 'sizeGuideScroll';

  /** True if the element sits inside a modal/overlay rather than page flow. */
  function inModal(el) {
    var node = el.parentElement;
    var depth = 0;
    while (node && node !== document.body && depth < 15) {
      if (window.getComputedStyle(node).position === 'fixed') return true;
      node = node.parentElement;
      depth += 1;
    }
    return false;
  }

  /**
   * On a phone the client wants the whole chart visible at once, not a wide table
   * to swipe through - so there the cells are scaled down (smaller type, tighter
   * padding) and headings may wrap onto two lines, which lets the chart shrink to
   * the modal's width. The scroll container above stays as a safety net for a chart
   * with more columns than can ever fit.
   */
  var mobileQuery = window.matchMedia('(max-width: 749px)');

  // The size-chart apps put their own inline styles on the table and its cells
  // (padding, widths). Those are snapshotted before we touch anything, and every
  // restyle starts from the snapshot - so the phone-only changes can be undone
  // exactly, and the desktop chart never loses the app's own styling.
  var originalStyles = typeof WeakMap === 'function' ? new WeakMap() : null;

  function restoreOriginalStyle(el) {
    if (!originalStyles) return;
    if (!originalStyles.has(el)) {
      originalStyles.set(el, el.style.cssText);
    } else {
      el.style.cssText = originalStyles.get(el);
    }
  }

  function styleChart(table) {
    var cells = table.querySelectorAll('th, td');
    restoreOriginalStyle(table);
    cells.forEach(restoreOriginalStyle);

    if (mobileQuery.matches) {
      // A real table stretched to the modal's width: as a display:block scroll box
      // the scaled-down chart stopped short of the edge and left empty space beside it.
      table.style.setProperty('display', 'table', 'important');
      table.style.setProperty('width', '100%', 'important');
      table.style.setProperty('max-width', '100%', 'important');
      table.style.setProperty('table-layout', 'auto', 'important');

      cells.forEach(function (cell) {
        cell.style.setProperty('white-space', 'normal', 'important');
        cell.style.setProperty('font-size', '1.1rem', 'important');
        cell.style.setProperty('line-height', '1.3', 'important');
        cell.style.setProperty('padding', '0.6rem 0.3rem', 'important');
        cell.style.setProperty('min-width', '0', 'important');
        cell.style.setProperty('width', 'auto', 'important');
      });
      return;
    }

    // Desktop: unchanged from before the phone fix.
    table.style.setProperty('display', 'block', 'important');
    table.style.setProperty('width', 'auto', 'important');
    table.style.setProperty('max-width', '100%', 'important');
    table.style.setProperty('overflow-x', 'auto', 'important');
    table.style.setProperty('-webkit-overflow-scrolling', 'touch');

    // Squeezed columns were breaking their text mid-heading ("Head Scarf / Shela"
    // over three lines, and the numbers under it split in two). Now that the chart
    // can scroll sideways there is no reason for a cell to wrap: each one keeps its
    // own single line and the table simply gets as wide as it needs to be.
    //
    // Long cells are left alone: a sentence-length note in a chart would
    // otherwise stretch the table to a width nobody can scroll through.
    cells.forEach(function (cell) {
      var text = (cell.textContent || '').trim();
      if (text.length > 30) return;
      cell.style.setProperty('white-space', 'nowrap', 'important');
    });
  }

  function makeChartTablesScrollable() {
    document.querySelectorAll('table').forEach(function (table) {
      if (table.dataset[SCROLL_FLAG] === 'true') {
        // The IN/CM toggle can re-render the rows inside an already styled table.
        var unstyled = originalStyles && Array.prototype.some.call(table.querySelectorAll('th, td'), function (cell) {
          return !originalStyles.has(cell);
        });
        if (unstyled) styleChart(table);
        return;
      }
      // Only charts in a modal. A table in the page flow is the theme's own
      // layout and is none of our business.
      if (!inModal(table)) return;

      table.dataset[SCROLL_FLAG] = 'true';
      styleChart(table);
    });
  }

  // Rotating the phone or resizing the window crosses the breakpoint.
  var onBreakpointChange = function () {
    document.querySelectorAll('table[data-size-guide-scroll="true"]').forEach(styleChart);
  };
  if (mobileQuery.addEventListener) {
    mobileQuery.addEventListener('change', onBreakpointChange);
  } else if (mobileQuery.addListener) {
    mobileQuery.addListener(onBreakpointChange);
  }

  /** The modal opens long after load, so re-check for a few seconds after a click. */
  function scheduleTableScroll() {
    [0, 150, 400, 800, 1500].forEach(function (delay) {
      window.setTimeout(makeChartTablesScrollable, delay);
    });
  }

  function handleClick(event) {
    event.preventDefault();

    var appTrigger = findAppTrigger();
    if (appTrigger) {
      appTrigger.click();
      scheduleTableScroll();
      return;
    }

    var wrapper = themePopup();
    if (wrapper) {
      bindCloseHandlers();
      openThemePopup(wrapper);
      scheduleTableScroll();
      return;
    }

    // Nothing to open. Hide the button so it is not a dead link, and leave a
    // console note so the missing chart is diagnosable from the storefront.
    var button = event.currentTarget;
    if (button && button.closest('.find_size_popup')) {
      button.closest('.find_size_popup').style.display = 'none';
    }
    console.warn('[size-guide] No size guide is configured for this product.');
  }

  /**
   * The apps render asynchronously, so re-check a few times before deciding the
   * button has nothing to open.
   */
  function refreshButtonVisibility() {
    var holders = document.querySelectorAll('.find_size_popup');
    if (!holders.length) return;
    var available = hasAnySizeGuide();
    holders.forEach(function (holder) {
      holder.style.display = available ? '' : 'none';
    });
  }

  function init() {
    hideDuplicateTriggers();

    document.querySelectorAll('.find_size_btn').forEach(function (button) {
      if (button.dataset.sizeGuideBound === 'true') return;
      button.dataset.sizeGuideBound = 'true';
      button.addEventListener('click', handleClick);
    });

    bindCloseHandlers();

    // Kiwi / MP inject their triggers after load; poll briefly, then settle.
    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;
      hideDuplicateTriggers();
      refreshButtonVisibility();
      makeChartTablesScrollable();
      if (attempts >= 10) window.clearInterval(timer);
    }, 700);
    refreshButtonVisibility();
    makeChartTablesScrollable();
    watchForCharts();
  }

  /**
   * A size chart can be opened at any point in the session, and an app may
   * re-render its modal each time. Watching for a <table> being added is
   * cheaper than polling forever, and catches every app without needing to
   * know its markup.
   */
  var chartObserver = null;

  function watchForCharts() {
    if (chartObserver || typeof MutationObserver !== 'function') return;

    chartObserver = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'TABLE' || node.querySelector('table') || node.closest('table')) {
            makeChartTablesScrollable();
            return;
          }
        }
      }
    });

    chartObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Dawn replaces the variant picker markup on variant change - rebind after.
  document.addEventListener('shopify:section:load', init);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      window.setTimeout(init, 0);
    });
  }
})();
